import { z } from 'zod';
import { id } from '@quizee/events';
import { FORMATS, type Format } from './content';
import { FORMAT_RULES } from './formats';

/**
 * Which brands an interaction runs on, and what a reader is shown of the results.
 *
 * A regional group with twelve titles wants to run one survey across all of them: one set of
 * questions, one combined result, but each title looking like itself. An interaction therefore has
 * an owning brand, which never changes and which can always render it, plus a rule saying which
 * other brands in the same org may render it too.
 *
 * The rule is not content. It says nothing about what a reader is asked, so it does not belong in
 * `interactionContentSchema`: putting it there would fold it into the approval fingerprint, and
 * moving a survey onto a thirteenth title would then invalidate a person's approval of words
 * nobody had touched. It lives beside `brand_id` on the interaction record instead.
 *
 * Sharing never crosses an org. Brands under one org are one publisher, with one contract, one
 * controller under GDPR and one set of aggregates. Nothing here takes an org id because nothing
 * here is allowed to reach past one.
 */

export const SHARE_MODES = ['brand', 'all', 'selected'] as const;
export type ShareMode = (typeof SHARE_MODES)[number];

/**
 * How many brands one interaction may run across.
 *
 * The published document carries a resolved theme per brand and is fetched on every page view, so
 * this is a size limit on that document as much as it is a product limit. A group with more titles
 * than this exists, and when one turns up the answer is to look at the document shape again rather
 * than to raise the number quietly.
 */
export const MAX_SHARED_BRANDS = 50;

const selectedBrandIds = z
  .array(id('brand'))
  .min(1, 'choose at least one title to share with')
  .max(MAX_SHARED_BRANDS)
  .refine((ids) => new Set(ids).size === ids.length, 'the same title is listed twice');

/**
 * Where an interaction may be published.
 *
 * `all` is a rule rather than a snapshot of the brands that existed when somebody clicked it. A
 * title launched next month is included, because that is what "all our titles" means to the person
 * choosing it, and a snapshot would quietly mean "all our titles as of last March". The cost is
 * that the set has to be resolved again on every publish (see `brandsForShare`), and that a brand
 * created after a publish does not appear on live pages until the interaction is republished:
 * `sharedBrandsChanged` is how the editor knows to say so.
 */
export const shareSchema = z.discriminatedUnion('mode', [
  /** Its own brand only. The default, and what every interaction written before this did. */
  z.object({ mode: z.literal('brand') }).strict(),
  /** Every brand in the org, including ones created after this was set. */
  z.object({ mode: z.literal('all') }).strict(),
  /** These titles, plus the owning brand, which can always render its own interaction. */
  z.object({ mode: z.literal('selected'), brand_ids: selectedBrandIds }).strict(),
]);

export type Share = z.infer<typeof shareSchema>;

/**
 * What an interaction with no `share` field means.
 *
 * Absent is not missing data, it is the answer: every interaction created before sharing existed
 * belongs to one brand, and nothing has to be migrated for that to keep being true.
 */
export const DEFAULT_SHARE: Share = Object.freeze({ mode: 'brand' });

export function effectiveShare(share: Share | undefined): Share {
  return share ?? DEFAULT_SHARE;
}

export const RESULTS_SCOPES = ['combined', 'per_brand'] as const;
export type ResultsScope = (typeof RESULTS_SCOPES)[number];

/**
 * Whether a reader on The Herald sees the whole group's percentages or only The Herald's.
 *
 * This is about the reader and nothing else. A publisher always sees both in the dashboard,
 * because an editor who ran a survey across twelve titles needs to know which readership engaged,
 * and that is a question the dashboard answers from events that already carry a brand id. It is
 * not a content setting and it is deliberately not modelled here.
 *
 * Absent means combined, which is also what a single brand interaction has always shown: with one
 * brand the two scopes are the same number.
 *
 * Neither scope merges readers. Device identity is first party to each publisher's domain (D7), so
 * somebody who reads two of the group's titles is two devices and two readers, and a combined
 * percentage is a sum of responses rather than a count of people. Nothing in this model should be
 * read as promising otherwise: responses aggregate, people do not.
 */
export const resultsScopeSchema = z.enum(RESULTS_SCOPES);

/** The sharing fields as they sit on an interaction record, both optional, both absent by default. */
export const sharingSettingsSchema = z
  .object({
    share: shareSchema.optional(),
    results_scope: resultsScopeSchema.optional(),
  })
  .strict();

export type SharingSettings = z.infer<typeof sharingSettingsSchema>;

export interface SharingCheckInput {
  readonly format: Format;
  /** The owning brand. Always able to render the interaction, whatever the share rule says. */
  readonly brand_id: string;
  readonly share?: Share | undefined;
  readonly results_scope?: ResultsScope | undefined;
}

/**
 * The rules that span more than one field, in the shape `checkFormatRules` already uses so a
 * record schema elsewhere can hang it off a `superRefine` without repeating any of it.
 *
 * No format is refused sharing. Each one was looked at rather than assumed: a poll shared across
 * twelve titles is one question with more voters, a trivia quiz is one set of answers marked the
 * same way everywhere, a personality quiz maps to the same outcomes, and a form's free text still
 * goes to the one org's vault with the brand it came from on the submission. There is no format
 * where a second masthead breaks the engine, so asserting one would be inventing a limit.
 *
 * What a format does constrain is the results scope, because a survey and a form show a reader no
 * results at all and there is then nothing to scope.
 */
export function checkSharing(input: SharingCheckInput, ctx: z.RefinementCtx): void {
  const rules = FORMAT_RULES[input.format];
  const share = effectiveShare(input.share);

  if (input.results_scope !== undefined && !rules.hasResults) {
    ctx.addIssue({
      code: 'custom',
      message: `a ${rules.label.toLowerCase()} shows a reader no results, so there is nothing to scope`,
      path: ['results_scope'],
    });
  }

  if (input.results_scope === 'per_brand' && !reachesMoreThanOneBrand(share, input.brand_id)) {
    ctx.addIssue({
      code: 'custom',
      message: 'per brand results need more than one brand to be able to see this',
      path: ['results_scope'],
    });
  }
}

/** The composed check, for a caller holding the four fields rather than a whole record. */
export const sharingSchema = sharingSettingsSchema
  .extend({ format: z.enum(FORMATS), brand_id: id('brand') })
  .strict()
  .superRefine(checkSharing);

/**
 * The brands an interaction renders on, resolved at publish time and in this one place.
 *
 * The owning brand comes first and is always present, so a rule can widen where an interaction
 * runs but never take it off its own masthead. `org_brand_ids` is the org's brands as they stand
 * right now, which is what makes `all` mean today's titles rather than the ones that existed when
 * somebody chose it.
 *
 * A selected brand that is no longer in the org is dropped rather than refused. There is no theme
 * left to resolve for a deleted masthead, and refusing would leave an interaction that cannot be
 * republished until somebody edits a list they cannot see.
 */
export function brandsForShare(input: {
  readonly owning_brand_id: string;
  readonly share?: Share | undefined;
  readonly org_brand_ids: readonly string[];
}): string[] {
  const share = effectiveShare(input.share);
  const resolved = [input.owning_brand_id];

  const add = (brandId: string): void => {
    if (!resolved.includes(brandId)) resolved.push(brandId);
  };

  if (share.mode === 'all') {
    for (const brandId of input.org_brand_ids) add(brandId);
  } else if (share.mode === 'selected') {
    for (const brandId of share.brand_ids) {
      if (input.org_brand_ids.includes(brandId)) add(brandId);
    }
  }

  return resolved;
}

/** Whether this brand may render this interaction, given the brands publishing resolved. */
export function canRenderOnBrand(brandId: string, resolvedBrandIds: readonly string[]): boolean {
  return resolvedBrandIds.includes(brandId);
}

/**
 * Whether what is live has fallen behind the share rule.
 *
 * The same idea as `consentIsStale`: a published document is frozen, the rule behind it is not, so
 * a brand created or removed since the last publish leaves the two disagreeing. Comparing the
 * brands a version was published for against the brands the rule resolves to now is how the editor
 * says "this needs republishing to reach The Argus" rather than leaving a title quietly missing.
 *
 * Order is not a difference. It is a set of brands, and publishing writes them in whatever order
 * the org's brands came back in.
 */
export function sharedBrandsChanged(
  published: readonly string[],
  resolved: readonly string[],
): boolean {
  if (published.length !== resolved.length) return true;
  const seen = new Set(published);
  return resolved.some((brandId) => !seen.has(brandId));
}

/**
 * Whether the rule can put this in front of a second brand at all.
 *
 * `all` counts even in an org with one brand today, because the rule is dynamic and the second
 * title may arrive next week. `selected` counts only when it names somebody other than the owner,
 * since a list holding just the owning brand is `{ mode: 'brand' }` written the long way.
 */
function reachesMoreThanOneBrand(share: Share, owningBrandId: string): boolean {
  if (share.mode === 'brand') return false;
  if (share.mode === 'all') return true;
  return share.brand_ids.some((brandId) => brandId !== owningBrandId);
}
